
/**
 * SEO Configuration System
 * 
 * This file contains the core SEO configuration for the application.
 * It defines the structure for SEO metadata and provides default values
 * and route-specific SEO data.
 */

export interface SEOData {
  title: string;
  description: string;
  keywords: string[];
  ogImage?: string;
  ogType?: 'website' | 'article';
  noIndex?: boolean;
}

export interface RouteSEO {
  path: string;
  seo: SEOData;
}

// Default SEO data to be used as fallback
export const defaultSEO: SEOData = {
  title: 'AI Wealth Manager - Smart Investment Solutions',
  description: 'AI-powered wealth management platform offering personalized investment strategies, portfolio optimization, and expert financial guidance.',
  keywords: ['wealth management', 'AI investment', 'portfolio optimization', 'financial planning'],
  ogImage: 'https://wealth-ai-future.web.app/coin_logo.png',
  ogType: 'website',
  noIndex: false,
};

// SEO configuration for different routes
export const routeSEO: RouteSEO[] = [
  {
    path: '/',
    seo: {
      title: 'AI Wealth Manager - AI works smarter, so will your money',
      description: 'Your personalized AI financial assistant that tracks your portfolio, delivers insights, and keeps you ahead. Get early access today.',
      keywords: ['AI financial assistant', 'portfolio tracking', 'investment insights', 'financial planning', 'wealth management'],
      ogType: 'website',
    },
  },
  {
    path: '/coming-soon',
    seo: {
      title: 'Coming Soon - AI Wealth Manager Features',
      description: 'Discover upcoming features of our AI-powered wealth management platform. Complete portfolio management across multiple asset classes.',
      keywords: ['wealth management features', 'AI investment', 'portfolio optimization', 'financial planning', 'coming soon'],
      ogType: 'website',
    },
  },
  {
    path: '/blog',
    seo: {
      title: '5 Things to Know Before the Market Opens - Market Insights',
      description: 'Get the latest market insights and key financial news before markets open. Daily analysis of stocks, trends, and investment opportunities.',
      keywords: ['market news', 'financial insights', 'stock market', 'investment news', 'market analysis'],
      ogType: 'website',
    },
  },
  {
    path: '/top-5-news',
    seo: {
      title: 'Top 5 Market News - Daily Stock Market Analysis & Insights',
      description: 'Stay ahead with daily top 5 market news, stock analysis, and investment insights. Get key market movers, Nifty trends, and financial updates before market opens.',
      keywords: [
        'top 5 market news',
        'daily stock market analysis',
        'nifty news today',
        'market movers',
        'stock market insights',
        'financial news india',
        'market opening news',
        'investment updates',
        'sensex nifty analysis',
        'indian stock market news',
        'market preview',
        'trading news'
      ],
      ogType: 'website',
    },
  },
  {
    path: '/auth',
    seo: {
      title: 'Sign In - AI Wealth Manager',
      description: 'Create an account or sign in to access your AI-powered wealth management dashboard. Get personalized investment insights and portfolio optimization.',
      keywords: ['sign in', 'login', 'wealth management account', 'investment dashboard'],
      ogType: 'website',
    },
  },
  {
    path: '/dashboard',
    seo: {
      title: 'Dashboard - AI Wealth Manager',
      description: 'View and manage your investment portfolio with AI-powered insights. Track performance, get recommendations, and optimize your wealth strategy.',
      keywords: ['investment dashboard', 'portfolio management', 'wealth tracking', 'investment insights'],
      noIndex: true,
    },
  },
  {
    path: '/privacy-policy',
    seo: {
      title: 'Privacy Policy - AI Wealth Manager',
      description: 'Learn about how AI Wealth Manager protects your data and privacy. Read our comprehensive privacy policy and data protection measures.',
      keywords: ['privacy policy', 'data protection', 'security', 'confidentiality'],
      ogType: 'website',
    },
  },
  {
    path: '/terms-of-use',
    seo: {
      title: 'Terms of Use - AI Wealth Manager',
      description: 'Review the terms and conditions for using AI Wealth Manager. Understand our service agreement and user responsibilities.',
      keywords: ['terms of use', 'terms and conditions', 'service agreement', 'user agreement'],
      ogType: 'website',
    },
  },
];

/**
 * Helper function to find SEO data for a given path
 * @param path - The current route path
 * @returns SEOData - The SEO configuration for the given path
 */
export const getSEOForPath = (path: string): SEOData => {
  const route = routeSEO.find(route => route.path === path);
  return route ? { ...defaultSEO, ...route.seo } : defaultSEO;
};
